import { preferenceEnabled, setPreference } from './preferences.ts';

/**
 * Il suono di chiamata.
 *
 * "Suono in-app ripetuto quando la scheda è aperta. `Audio` va sbloccato da un
 * gesto utente: il pulsante «Inizia turno» fa login e sblocca l'audio"
 * (sezione 5.7). Qui il gesto è **"entra in turno adesso"**, che è anche il
 * momento giusto: da lì in poi possono arrivare chiamate.
 *
 * Generato, non caricato: due note brevi con l'oscillatore del browser. Un file
 * audio sarebbe un asset in più da servire, da mettere in cache e da tenere
 * allineato, per mezzo secondo di suono.
 *
 * Ogni segnale sonoro ha un equivalente visivo, e viceversa (sezione 7bis): il
 * riquadro della chiamata in arrivo e l'annuncio `assertive` esistono comunque,
 * e il suono si può spegnere senza perdere niente. I volontari sordi esistono
 * quanto quelli ciechi.
 */

/** Due note, come uno squillo. Ripetute finché la chiamata è lì. */
const TONE_HZ = [880, 660] as const;
const TONE_SECONDS = 0.18;
const GAP_SECONDS = 0.12;
const REPEAT_MS = 2200;

type AudioContextConstructor = new () => AudioContext;

let context: AudioContext | undefined;
let ringing: ReturnType<typeof setInterval> | undefined;

function contextConstructor(): AudioContextConstructor | undefined {
  const scope = globalThis as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext;
}

export function soundSupported(): boolean {
  return contextConstructor() !== undefined;
}

/** Il suono è acceso? Preferenza di questo dispositivo, non del profilo. */
export function soundEnabled(): boolean {
  return preferenceEnabled('console.sound');
}

export function setSoundEnabled(enabled: boolean): void {
  setPreference('console.sound', enabled);
  if (!enabled) stopRinging();
}

/**
 * Sblocca l'audio. Va chiamata **dentro** un gestore di evento di un gesto.
 *
 * I browser tengono l'`AudioContext` sospeso finché la persona non tocca
 * qualcosa: senza questo, il primo squillo sarebbe silenzioso e nessuno
 * saprebbe perché.
 *
 * Restituisce una promessa perché `resume()` è asincrona: su Safari e su iOS
 * non è istantanea, e leggere lo stato subito dopo diceva "non sbloccato" a chi
 * lo aveva appena sbloccato — una diagnosi falsa, con il rimedio giusto
 * attaccato. La chiamata a `resume()` avviene comunque **dentro** il gesto, che
 * è ciò che il browser guarda.
 *
 * `resume()` da solo non bastava: "entra in turno adesso" sblocca il
 * contesto (`state` passa a `running`), ma finché nessun suono viene prodotto
 * *dentro* quel gesto, alcuni browser continuano a trattare lo squillo che
 * arriva dopo — da un `setInterval`, non da un tocco — come riproduzione
 * automatica, e lo azzittiscono in silenzio: `state` resta "running", niente
 * si sente, e niente in questo modulo se ne accorge. Il pulsante "prova il
 * suono" delle Notifiche funzionava perché produce un suono vero nello stesso
 * gesto; "entra in turno" no. Qui si genera un tono impercettibile (guadagno
 * quasi a zero, un decimo di secondo) apposta per contare come quel suono, e
 * senza che chi entra in turno senta un bip a ogni volta.
 */
export async function unlockSound(): Promise<boolean> {
  const Constructor = contextConstructor();
  if (!Constructor) return false;
  context ??= new Constructor();
  try {
    await context.resume?.();
  } catch {
    // Un contesto che non riparte non è un errore da mostrare: la schermata
    // dice che il suono si sblocca entrando in turno, ed è la verità.
  }
  if (context.state === 'running') primeAudioGraph(context);
  return context.state === 'running';
}

/** Il tono silenzioso che stabilisce l'attività reale del grafo audio. */
function primeAudioGraph(active: AudioContext): void {
  const oscillator = active.createOscillator();
  const gain = active.createGain();
  gain.gain.value = 0.00001;
  oscillator.connect(gain).connect(active.destination);
  oscillator.start();
  oscillator.stop(active.currentTime + 0.1);
}

/** L'audio è pronto a suonare senza altri gesti? */
export function soundUnlocked(): boolean {
  return context?.state === 'running';
}

/**
 * Il browser sospende da sé un contesto già sbloccato — schermo bloccato,
 * un'altra app in primo piano su un telefono, la scheda tenuta in background
 * a lungo — e da lì lo squillo tace di nuovo senza che nessuno lo chieda:
 * `state` non torna "running" da solo. Riportarlo indietro **non** richiede
 * un nuovo gesto (a differenza dello sblocco iniziale, sez. 5.7): è la stessa
 * interruzione a cessare, non un nuovo permesso da negoziare — per questo
 * `resume()` qui non è seguita da `primeAudioGraph()`.
 *
 * Recidiva di BI3: quel fix risolveva solo il primo sblocco, non un contesto
 * che torna sospeso più tardi nello stesso turno.
 */
function attemptResume(active: AudioContext): void {
  void active.resume?.().catch(() => undefined);
}

/**
 * Tiene pronto il suono per tutta la durata di un turno: riattiva un
 * contesto che il browser ha sospeso da sé, e sblocca da zero un contesto
 * mai creato.
 *
 * Va agganciata da chi mostra la console del turno (`ConsolePage`), non a
 * tempo di modulo: `sound.ts` è importato anche da schermate che non
 * gestiscono mai uno squillo (`NotificationsPage` per il solo pulsante
 * "prova il suono", e nello stesso bundle senza code-splitting anche
 * `AdminPage`, `NetworkPage`, ecc.) — un listener a livello di documento
 * agganciato lì per sempre creerebbe un `AudioContext` inerte anche per un
 * referente che guarda solo un aggregato (vincolo 7), per un motivo di
 * architettura del bundle e non per una decisione. Il chiamante la sgancia
 * smontando la schermata.
 *
 * Due gesti diversi, entrambi coperti:
 * - **il browser sospende un contesto già sbloccato** — schermo bloccato,
 *   un'altra app in primo piano, backgrounding — e da lì lo squillo tace di
 *   nuovo senza che nessuno lo chieda: riattivarlo **non** richiede un nuovo
 *   gesto (a differenza dello sblocco iniziale, sez. 5.7), è la stessa
 *   interruzione che cessa, non un nuovo permesso da negoziare. Recidiva di
 *   BI3 (BJ1, `docs/DECISIONI.md`).
 * - **il contesto non esiste affatto**: "entra in turno adesso" è il gesto
 *   pensato dalla sezione 5.7, ma chi riapre la console già in turno (la
 *   scheda ricaricata, il telefono l'ha scaricata dalla memoria, un service
 *   worker che riporta qui dopo una push) non vede mai quel pulsante — lo
 *   stato "in turno" arriva dal server — e `unlockSound()` non viene mai
 *   chiamata. Qualunque tocco reale sulla pagina è comunque un gesto
 *   valido agli occhi del browser: lo si intercetta finché il suono non
 *   risulta sbloccato. Stesso sintomo di BJ1, causa diversa (BJ4).
 */
export function armAutoUnlock(): () => void {
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && context && context.state !== 'running') {
      attemptResume(context);
    }
  };
  const onInteraction = (): void => {
    if (!soundUnlocked()) void unlockSound();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  document.addEventListener('pointerdown', onInteraction);
  document.addEventListener('keydown', onInteraction);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
  };
}

/** Uno squillo solo: è anche il pulsante "prova il suono". */
export function playRing(): void {
  if (!soundEnabled()) return;
  const active = context;
  // Non ancora sbloccato, o tornato sospeso da solo: niente suono adesso, ma
  // si prova a riattivarlo per il prossimo giro invece di restare muti finché
  // qualcuno non preme "prova il suono" a mano.
  if (active?.state !== 'running') {
    if (active) attemptResume(active);
    return;
  }

  TONE_HZ.forEach((frequency, index) => {
    const startsAt = active.currentTime + index * (TONE_SECONDS + GAP_SECONDS);
    const oscillator = active.createOscillator();
    const gain = active.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    // Attacco e rilascio morbidi: un'onda tagliata di netto fa un click, che
    // su un telefono vicino all'orecchio è sgradevole.
    gain.gain.setValueAtTime(0.0001, startsAt);
    gain.gain.exponentialRampToValueAtTime(0.25, startsAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + TONE_SECONDS);

    oscillator.connect(gain).connect(active.destination);
    oscillator.start(startsAt);
    oscillator.stop(startsAt + TONE_SECONDS + 0.02);
  });
}

/**
 * Squilla finché non gli si dice di smettere.
 *
 * Ripetuto, perché una chiamata dura venticinque secondi e chi è in cucina non
 * torna al primo suono. Non c'è nessun lampeggio ad accompagnarlo: la sezione
 * 7bis lo vieta, e il riquadro compare senza animazione.
 */
export function startRinging(): void {
  if (ringing !== undefined) return;
  playRing();
  ringing = setInterval(playRing, REPEAT_MS);
}

export function stopRinging(): void {
  if (ringing === undefined) return;
  clearInterval(ringing);
  ringing = undefined;
}

/** Solo per i test: riporta il modulo allo stato di partenza. */
export function resetSoundForTests(): void {
  stopRinging();
  context = undefined;
}
